import { z } from 'zod';
import { isoDateSchema, softDeletableSchema, uuidSchema } from './common';

// Dancer — child entity of family. Per plan §6.5 (COPPA data minimization):
// dancer records hold NO email, NO phone, NO address.
// Standalone login is gated by COPPA consent + dob >= 13yo.

export const dancerStatusSchema = z.enum(['active', 'inactive', 'withdrawn']);
export type DancerStatus = z.infer<typeof dancerStatusSchema>;

export const dancerInputSchema = z.object({
  family_id: uuidSchema,
  first_name: z.string().trim().min(1).max(80),
  last_name: z.string().trim().min(1).max(80),
  preferred_name: z.string().trim().max(80).optional(),
  date_of_birth: isoDateSchema,
  pronouns: z.string().trim().max(40).optional(),
  status: dancerStatusSchema.default('active'),
  allergies: z.string().trim().max(500).optional(),
  notes: z.string().trim().max(2000).optional(),
  // Parent consent for standalone login at 13+ (COPPA).
  // false by default — explicit opt-in per plan §6.5.
  standalone_login_consent: z.boolean().default(false),
});
export type DancerInput = z.infer<typeof dancerInputSchema>;

export const dancerSchema = softDeletableSchema.merge(dancerInputSchema).extend({
  auth_user_id: uuidSchema.nullable(),
  // Derived billing flags — DB-maintained, not set on create.
  costume_due: z.boolean(),
  payment_due: z.boolean(),
});
export type Dancer = z.infer<typeof dancerSchema>;

// Helper: is this dancer eligible for standalone login right now?
// (Both consent recorded AND age >= 13.)
// Uses calendar-date comparison, not millisecond duration, so DST and
// timezone offsets cannot push a dancer over the threshold a day early
// or keep them locked out a day late. COPPA boundary correctness matters.
export function canStandaloneLogin(
  d: Pick<Dancer, 'date_of_birth' | 'standalone_login_consent'>,
  asOf: Date = new Date(),
): boolean {
  if (!d.standalone_login_consent) return false;
  return computeAgeYears(d.date_of_birth, asOf) >= 13;
}

// Pure age-in-years calculator. Exported for unit testing of the boundary
// conditions (day before / day of / day after birthday).
export function computeAgeYears(dob: string, asOf: Date = new Date()): number {
  // Parse YYYY-MM-DD as a date in the local calendar (no time component).
  const [y, m, d] = dob.split('-').map(Number);
  if (!y || !m || !d) return 0;

  const dobY = y;
  const dobM = m - 1; // JS months are 0-indexed
  const dobD = d;

  const asY = asOf.getFullYear();
  const asM = asOf.getMonth();
  const asD = asOf.getDate();

  let age = asY - dobY;
  // Haven't reached this year's birthday yet?
  if (asM < dobM || (asM === dobM && asD < dobD)) {
    age -= 1;
  }
  return age;
}

export const dancerImportRowSchema = z.object({
  family_email: z.string().trim().toLowerCase().email(),
  first_name: z.string().trim().min(1),
  last_name: z.string().trim().min(1),
  preferred_name: z.string().trim().optional().default(''),
  dob: isoDateSchema,
  pronouns: z.string().trim().optional().default(''),
  status: dancerStatusSchema.default('active'),
});
export type DancerImportRow = z.infer<typeof dancerImportRowSchema>;
